import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Alert,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  addTransHeaderExpenseBundle,
  amendTransHeaderExpense,
  canChangeTransHeaderExpenseType,
  cancelTransHeaderExpense,
  createTransHeaderExpense,
  createTransHeaderExpensePayment,
  createTransHeaderExpenseRow,
  deleteTransHeaderExpensePayment,
  deleteTransHeaderExpenseRow,
  deleteTransHeaderExpense,
  getTransHeaderExpense,
  getTransHeaderExpenseHeader,
  getTransHeaderExpensePayment,
  getTransHeaderExpensePayments,
  getTransHeaderExpenseRows,
  getTransHeaderExpenseTotals,
  getTransTypes,
  reviewTransHeaderExpense,
  saveTransHeaderExpenseReview,
  submitTransHeaderExpense,
  updateTransHeaderExpense,
  updateTransHeaderExpensePayment,
  updateTransHeaderExpenseRow,
} from "src/apis/controllers/expenses";
import { getFiles } from "src/apis/controllers/files";
import { FileProvider, useFileContext } from "src/context/FilesV2";
import { useLoading } from "src/hooks/useLoading";
import { formatFloat } from "src/qubCommon/wijmoHelpers/numberMethods";
import {
  deleteBundle,
  deleteFile,
  downloadFile,
  formatFiles,
  uploadFiles,
} from "src/util";
import ChangeStatusDialog from "./ChangeStatusDialog";
import ExpenseActions from "./ExpenseActions";
import ExpenseAttachments from "./ExpenseAttachments";
import ExpenseDetailDialog from "./ExpenseDetailDialog";
import ExpenseDetailsGrid, { REVIEW_STATUS_IDS } from "./ExpenseDetailsGrid";
import ExpenseHeaderDialog from "./ExpenseHeaderDialog";
import ExpenseTransactionHeader, {
  ExpensePaymentsPanel,
} from "./ExpenseTransactionHeader";
import FinalReviewDialog from "./FinalReviewDialog";
import PaymentDialog from "./PaymentDialog";
import PaymentAttachmentsReadOnly from "./PaymentAttachmentsReadOnly";
import {
  buildTransHeaderExpensePayload,
  getResponseMessage,
  getResponsePayload,
  getTransHeaderIdFromResponse,
  isErrorResponse,
  isWarningResponse,
} from "./headerPayload";

const EXPENSE_ATTACHMENTS_PROJECT_TABLE_ID = 18;
const OPEN_EXPENSE_DETAIL_DIALOG_STATE_KEY = "openExpenseDetailDialog";

const isTrueFlag = (value) =>
  value === true || value?.toString?.().trim?.().toLowerCase?.() === "true";

const sectionSummaryCurrencyFormatter = new Intl.NumberFormat("el-GR", {
  style: "currency",
  currency: "EUR",
});

const parseSectionSummaryAmount = (value) => {
  if (value === undefined || value === null || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;

  const normalized = value
    .toString()
    .replace(/\s/g, "")
    .replace("€", "")
    .trim();

  if (!normalized) return 0;

  if (
    normalized.includes(",") ||
    /^(\d{1,3}\.)+\d{3}$/.test(normalized)
  ) {
    const amount = formatFloat(normalized);
    return Number.isFinite(amount) ? amount : 0;
  }

  const amount = Number(normalized);
  return Number.isFinite(amount) ? amount : 0;
};

const sumSectionAmounts = (items, fieldName) =>
  (Array.isArray(items) ? items : []).reduce(
    (sum, item) => sum + parseSectionSummaryAmount(item?.[fieldName]),
    0,
  );

const getSectionSummaryText = (count, singularLabel, pluralLabel, total) =>
  `${count} ${count === 1 ? singularLabel : pluralLabel} - Σύνολο: ${sectionSummaryCurrencyFormatter.format(total)}`;

const ExpenseSectionAccordionTitle = ({ title, subtitle, collapsedAction }) => (
  <Stack
    direction={{ xs: "column", sm: "row" }}
    gap={{ xs: 0.25, sm: 1.5 }}
    alignItems={{ xs: "flex-start", sm: "baseline" }}
    sx={{ flex: 1, minWidth: 0 }}
  >
    <Typography color="primary" sx={{ fontWeight: 1000 }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography color="primary" sx={{ fontSize: 13, fontWeight: 500 }}>
        {subtitle}
      </Typography>
    )}
    {collapsedAction && (
      <Box sx={{ mt: { xs: 0.5, sm: 0 } }}>
        {collapsedAction}
      </Box>
    )}
  </Stack>
);

const ExpenseSectionAccordion = ({
  title,
  subtitle,
  collapsedAction,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = () => {
    setIsOpen((currentValue) => !currentValue);
  };

  return (
    <Box
      className="qub-simple-accordion"
      sx={{
        border: "1px solid #ccc",
        overflow: "hidden",
      }}
    >
      <Box
        onClick={handleToggle}
        sx={{
          alignItems: "center",
          cursor: "pointer",
          display: "flex",
          gap: 1,
          justifyContent: "space-between",
          px: "15px",
          py: "10px",
        }}
      >
        <ExpenseSectionAccordionTitle
          title={title}
          subtitle={subtitle}
          collapsedAction={!isOpen ? collapsedAction : null}
        />
        <ExpandMoreIcon
          sx={{
            flexShrink: 0,
            transition: "transform 0.3s ease",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </Box>
      <Collapse in={isOpen} timeout="auto">
        <Box
          sx={{
            backgroundColor: "#fff",
            px: "15px",
            py: "10px",
          }}
        >
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};

const DEFAULT_EXPENSE_TRANS_TYPE_MODE_CONFIG = {
  appartmentMode: "",
  childMode: "",
  employeeMode: "",
  dateFromMode: "",
  dateToMode: "",
  supplierMode: "",
  hideVat: false,
};

const normalizeMode = (value) => value?.toString?.().trim?.() ?? "";

const normalizeBoolean = (value) =>
  value === true || value?.toString?.().trim?.().toLowerCase?.() === "true";

const getTransTypeModeConfig = (transType) => ({
  appartmentMode: normalizeMode(
    transType?.appartmenMode ??
    transType?.appartmentMode ??
    transType?.AppartmenMode ??
    transType?.AppartmentMode,
  ),
  childMode: normalizeMode(transType?.childMode ?? transType?.ChildMode),
  employeeMode: normalizeMode(
    transType?.employeeMode ?? transType?.EmployeeMode,
  ),
  dateFromMode: normalizeMode(
    transType?.dateFromMode ?? transType?.DateFromMode,
  ),
  dateToMode: normalizeMode(transType?.dateToMode ?? transType?.DateToMode),
  supplierMode: normalizeMode(
    transType?.supplierMode ?? transType?.SupplierMode,
  ),
  hideVat: normalizeBoolean(
    transType?.hideVat ?? transType?.hidevat ?? transType?.HideVat,
  ),
});

const getTransTypeLookupPayload = (res) => {
  const payload = getResponsePayload(res);
  return Array.isArray(payload) ? payload : [];
};

const findTransTypeLookup = (transTypes = [], transTypeId) => {
  if (transTypeId === undefined || transTypeId === null || transTypeId === "") {
    return null;
  }

  return (
    transTypes.find(
      (transType) => transType?.id?.toString() === transTypeId?.toString(),
    ) ?? null
  );
};

const normalizeDate = (value) => (value ? new Date(value) : null);

const expenseDetailTitleDateFormatter = new Intl.DateTimeFormat("el-GR");

const formatExpenseDetailTitleDate = (value) => {
  if (!value) return "";

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return value?.toString?.() ?? "";

  return expenseDetailTitleDateFormatter.format(date);
};



const getExpenseDetailDialogTitle = (transaction, fallbackMhdName) => {
  const documentTitle = [
    transaction?.mhdAbbr,
    transaction?.transTypeName,
  ]
    .filter(Boolean)
    .join(" ");
  const transDate =
    transaction?.transDateFormatted ??
    formatExpenseDetailTitleDate(transaction?.transDate);

  return ["ΑΝΑΛΥΣΗ ΔΑΠΑΝΗΣ", documentTitle, transaction?.transNumber, transDate]
    .filter(Boolean)
    .join(" - ");
};

const emptyToNull = (value) => {
  const nextValue = value?.toString?.().trim?.() ?? "";
  return nextValue === "" ? null : nextValue;
};

const normalizeDetailRow = (row) => ({
  ...row,
  expStatusId: row?.expStatusId ?? row?.ExpStatusId ?? null,
  expStatusAbbr: row?.expStatusAbbr ?? row?.ExpStatusAbbr,
  statusComments:
    row?.expStatusComments ??
    row?.ExpStatusComments ??
    row?.statusComments ??
    row?.StatusComments ??
    "",
  dateFrom: normalizeDate(row?.dateFrom ?? row?.DateFrom),
  dateTo: normalizeDate(row?.dateTo ?? row?.DateTo),
});

const getRowsPayload = (res) => {
  const payload = getResponsePayload(res);
  return Array.isArray(payload) ? payload.map(normalizeDetailRow) : [];
};

const parseReviewAmount = (value) => {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;

  const normalized = value
    .toString()
    .replace(/\s/g, "")
    .replace("€", "")
    .trim();

  if (!normalized) return null;

  if (
    normalized.includes(",") ||
    /^(\d{1,3}\.)+\d{3}$/.test(normalized)
  ) {
    const amount = formatFloat(normalized);
    return Number.isFinite(amount) ? amount : null;
  }

  const amount = Number(normalized);
  return Number.isFinite(amount) ? amount : null;
};

const normalizeReviewStatusId = (value) => {
  if (value === undefined || value === null || value === "") return null;

  const statusId = Number(value);
  return Number.isFinite(statusId) ? statusId : null;
};

const buildReviewRowsPayload = (rows = []) =>
  rows.map((row) => {
    const statusId = normalizeReviewStatusId(row?.expStatusId);

    return {
      id: row.id,
      statusId,
      approvedAmount:
        statusId === REVIEW_STATUS_IDS.partiallyApproved
          ? parseReviewAmount(row?.approvedAmount)
          : null,
      statusComments: emptyToNull(row?.statusComments),
    };
  });

const getReviewRowLabel = (row, index) =>
  row?.expTypeName ||
  row?.childFullName ||
  row?.employeeFullName ||
  `#${index + 1}`;

const validateReviewRows = (rows = []) => {
  for (const [index, row] of rows.entries()) {
    const statusId = normalizeReviewStatusId(row?.expStatusId);

    if (statusId !== REVIEW_STATUS_IDS.partiallyApproved) continue;

    const amount = parseReviewAmount(row?.approvedAmount);
    const totalAmount = Number(row?.totalAmount ?? 0);

    if (amount === null) {
      return `Συμπληρώστε εγκεκριμένο ποσό στη γραμμή ${getReviewRowLabel(
        row,
        index,
      )}.`;
    }

    if (amount <= 0 || amount >= totalAmount) {
      return `Το μερικώς εγκεκριμένο ποσό στη γραμμή ${getReviewRowLabel(
        row,
        index,
      )} πρέπει να είναι μεγαλύτερο από 0 και μικρότερο από το σύνολο.`;
    }
  }

  return null;
};

const getInferredFinalReviewStatusId = (rows = []) => {
  if (rows.length === 0) return "";

  const statusIds = rows.map((row) =>
    normalizeReviewStatusId(row?.expStatusId),
  );

  if (statusIds.every((statusId) => statusId === REVIEW_STATUS_IDS.approved)) {
    return REVIEW_STATUS_IDS.approved.toString();
  }

  if (statusIds.every((statusId) => statusId === REVIEW_STATUS_IDS.rejected)) {
    return REVIEW_STATUS_IDS.rejected.toString();
  }

  return REVIEW_STATUS_IDS.partiallyApproved.toString();
};

const mergeRowsWithReviewDraft = (rows = [], draftRows = []) => {
  const draftById = new Map(draftRows.map((row) => [row.id, row]));

  return rows.map((row) => {
    const draftRow = draftById.get(row.id);
    const hasResponseComments =
      row.statusComments !== undefined &&
      row.statusComments !== null &&
      row.statusComments !== "";

    return {
      ...row,
      statusComments: hasResponseComments
        ? row.statusComments
        : (draftRow?.statusComments ?? ""),
    };
  });
};

const getAttachmentRequest = (transHeaderId) => ({
  DataKey: "MHD_TransHeader_Expenses",
  DataValue: null,
  RecordId: null,
  RecordGuid: transHeaderId,
  ProjectTableId: EXPENSE_ATTACHMENTS_PROJECT_TABLE_ID,
});

const uniqueBundleIds = (bundleIds) => [
  ...new Set(bundleIds.filter((bundleId) => !!bundleId)),
];

const getBundleIdsFromUploadResponse = (res) => {
  const payload = getResponsePayload(res);

  if (Array.isArray(payload)) {
    return uniqueBundleIds(
      payload.flatMap((item) => [
        item?.bundleId,
        item?.BundleId,
        ...(Array.isArray(item?.bundleIds) ? item.bundleIds : []),
        ...(Array.isArray(item?.BundleIds) ? item.BundleIds : []),
      ]),
    );
  }

  return uniqueBundleIds([
    ...(Array.isArray(payload?.bundleIds) ? payload.bundleIds : []),
    ...(Array.isArray(payload?.BundleIds) ? payload.BundleIds : []),
  ]);
};

const getUnsavedBundleIds = (fileGroups = []) =>
  uniqueBundleIds(
    fileGroups.reduce((acc, group) => {
      const groupBundleIds =
        group.bundles
          ?.filter(
            (bundle) =>
              bundle?.files?.length > 0 &&
              (!bundle?.saved ||
                bundle.files.some(
                  (file) => !file?.dbId && file?.saved !== true,
                )),
          )
          .map((bundle) => bundle.id) ?? [];

      return [...acc, ...groupBundleIds];
    }, []),
  );

const getValidationMessage = (res, fallback) => {
  const payload = getResponsePayload(res);
  const validationErrors = payload?.validationErrors ?? res?.validationErrors;

  if (Array.isArray(validationErrors) && validationErrors.length > 0) {
    const messages = validationErrors
      .flatMap((error) =>
        (typeof error === "string"
          ? error
          : (error?.message ?? error?.Message ?? "")
        )
          .split(/<br\s*\/?>|&lt;br\s*\/?&gt;|\r?\n/gi)
          .map((message) => message.trim()),
      )
      .filter(Boolean);

    if (messages.length > 0) {
      return (
        <div
          style={{
            display: "grid",
            gap: "2px",
            lineHeight: 1.35,
            textAlign: "left",
            whiteSpace: "normal",
          }}
        >
          <div style={{ fontWeight: 700 }}>
            {getResponseMessage(res, fallback)}
          </div>
          {messages.map((message, index) => (
            <div key={`${message}-${index}`} style={{ margin: 0, padding: 0 }}>
              {message}
            </div>
          ))}
        </div>
      );
    }
  }

  return getResponseMessage(res, fallback);
};

const isShortcutTypingTarget = (target) => {
  const tagName = target?.tagName;

  return (
    target?.isContentEditable ||
    tagName === "INPUT" ||
    tagName === "TEXTAREA" ||
    tagName === "SELECT"
  );
};

const hasOpenShortcutOverlay = () => {
  if (typeof document === "undefined") return false;

  return Boolean(
    document.querySelector(
      '[role="dialog"], .MuiPopover-root, .MuiMenu-root, .MuiAutocomplete-popper, .react-datepicker',
    ),
  );
};

const ExpenseTransactionDetailsContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { applicationId, transHeaderId } = useParams();
  const { setLoading } = useLoading();
  const { control: filesControl, fileGroups } = useFileContext();
  const isNewTransaction = !transHeaderId;
  const [isHeaderDialogOpen, setIsHeaderDialogOpen] =
    useState(isNewTransaction);
  const [headerDialogMode, setHeaderDialogMode] = useState(
    isNewTransaction ? "create" : "edit",
  );
  const [isHeaderSaving, setIsHeaderSaving] = useState(false);
  const [isAttachmentSaving, setIsAttachmentSaving] = useState(false);
  const [isExpenseRowSaving, setIsExpenseRowSaving] = useState(false);
  const [isPaymentSaving, setIsPaymentSaving] = useState(false);
  const [isReviewSaving, setIsReviewSaving] = useState(false);
  const [isFinalReviewSaving, setIsFinalReviewSaving] = useState(false);
  const [isCancelSaving, setIsCancelSaving] = useState(false);
  const [typeChangeState, setTypeChangeState] = useState({
    can: true,
    message: null,
  });
  const [isDeleteWarningOpen, setIsDeleteWarningOpen] = useState(false);
  const [
    isUnsavedAttachmentsBackWarningOpen,
    setIsUnsavedAttachmentsBackWarningOpen,
  ] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [expenseDialogState, setExpenseDialogState] = useState({
    open: false,
    mode: "create",
    row: null,
  });
  const [paymentDialogState, setPaymentDialogState] = useState({
    open: false,
    mode: "create",
    payment: null,
  });
  const [reviewDialogState, setReviewDialogState] = useState({
    open: false,
    statusId: "",
    comments: "",
  });
  const [cancelDialogState, setCancelDialogState] = useState({
    open: false,
    comments: "",
  });
  const [isChangeStatusDialogOpen, setIsChangeStatusDialogOpen] =
    useState(false);
  const [isChangeStatusSaving, setIsChangeStatusSaving] = useState(false);
  const [expenseRowDeleteCandidate, setExpenseRowDeleteCandidate] =
    useState(null);
  const [paymentDeleteCandidate, setPaymentDeleteCandidate] = useState(null);
  const [transaction, setTransaction] = useState(null);
  const [expenseRows, setExpenseRows] = useState([]);
  const [paymentAttachmentsRefreshKey, setPaymentAttachmentsRefreshKey] =
    useState(0);
  const [transTypeLookups, setTransTypeLookups] = useState([]);
  const [expenseModeConfig, setExpenseModeConfig] = useState(
    DEFAULT_EXPENSE_TRANS_TYPE_MODE_CONFIG,
  );
  const [errorMessage, setErrorMessage] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(isNewTransaction);

  const mhdName =
    transaction?.mhdAbbr ??
    transaction?.mhdName ??
    location.state?.mhdName ??
    "ΜΗΔ xxx";
  const showApprovedAmount =
    transaction?.settings?.showApprovedAmount === true ||
    transaction?.showApprovedAmount === true;
  const showReviewStatus =
    transaction?.settings?.showReviewStatus === true ||
    transaction?.showReviewStatus === true;
  const canReview = transaction?.settings?.review?.can === true;
  const canAddExpenseRow = transaction?.settings?.addExpense?.can === true;
  const expenseActionsDisabled =
    isReviewSaving ||
    isFinalReviewSaving ||
    isCancelSaving ||
    isChangeStatusSaving;
  const canAddPayment =
    transaction?.settings?.addPayment?.can === true ||
    transaction?.canAddPayment === true;
  const shouldOpenExpenseDetailAfterHeaderCreate =
    location.state?.[OPEN_EXPENSE_DETAIL_DIALOG_STATE_KEY] === true;
  const isShortcutBlocked =
    isHeaderDialogOpen ||
    expenseDialogState.open ||
    paymentDialogState.open ||
    reviewDialogState.open ||
    cancelDialogState.open ||
    isChangeStatusDialogOpen ||
    !!expenseRowDeleteCandidate ||
    !!paymentDeleteCandidate ||
    isDeleteWarningOpen ||
    isUnsavedAttachmentsBackWarningOpen ||
    !!deleteConfirmation;

  useEffect(() => {
    const transTypeLookup = findTransTypeLookup(
      transTypeLookups,
      transaction?.transTypeId,
    );

    setExpenseModeConfig(
      transTypeLookup
        ? getTransTypeModeConfig(transTypeLookup)
        : DEFAULT_EXPENSE_TRANS_TYPE_MODE_CONFIG,
    );
  }, [transaction?.transTypeId, transTypeLookups]);

  const initializeAttachments = (files) => {
    try {
      const formattedFiles = formatFiles(files ?? []);
      filesControl.initialize(formattedFiles ?? []);
    } catch (error) {
      filesControl.initialize([]);
      enqueueSnackbar("Δεν φορτώθηκαν τα συνημμένα.", {
        variant: "warning",
      });
    }
  };

  const fetchAttachments = async () => {
    console.log('got in here')
    if (!transHeaderId) {
      filesControl.initialize([]);
      return;
    }

    const filesRes = await getFiles(getAttachmentRequest(transHeaderId));
    initializeAttachments(filesRes?.data);
  };

  const reloadHeader = async () => {
    if (!transHeaderId) return;

    const res = await getTransHeaderExpenseHeader(transHeaderId);

    if (isErrorResponse(res)) {
      enqueueSnackbar(
        getResponseMessage(res, "Δεν φορτώθηκαν τα στοιχεία παραστατικού."),
        { variant: "error" },
      );
      return;
    }

    const payload = getResponsePayload(res);
    setTransaction((currentTransaction) => ({
      ...currentTransaction,
      ...payload,
    }));
  };

  const reloadExpenseRows = async () => {
    if (!transHeaderId) return;

    const res = await getTransHeaderExpenseRows(transHeaderId);

    if (isErrorResponse(res)) {
      enqueueSnackbar(
        getResponseMessage(res, "Δεν φορτώθηκαν οι γραμμές δαπάνης."),
        { variant: "warning" },
      );
      setExpenseRows([]);
      return;
    }

    const rows = getRowsPayload(res);
    setExpenseRows((currentRows) =>
      mergeRowsWithReviewDraft(rows, currentRows),
    );
  };

  const reloadTotals = async () => {
    if (!transHeaderId) return;

    const res = await getTransHeaderExpenseTotals(transHeaderId);

    if (isErrorResponse(res)) {
      enqueueSnackbar(
        getResponseMessage(res, "Δεν φορτώθηκαν τα σύνολα δαπάνης."),
        { variant: "warning" },
      );
      return;
    }

    const payload = getResponsePayload(res);
    setTransaction((currentTransaction) => ({
      ...currentTransaction,
      ...payload,
    }));
  };

  const reloadPayments = async () => {
    if (!transHeaderId) return;

    const res = await getTransHeaderExpensePayments(transHeaderId);

    if (isErrorResponse(res)) {
      enqueueSnackbar(getResponseMessage(res, "Δεν φορτώθηκαν οι πληρωμές."), {
        variant: "warning",
      });
      return;
    }

    const payload = getResponsePayload(res);
    setTransaction((currentTransaction) => ({
      ...(currentTransaction ?? {}),
      paidAmount: payload?.paidAmount,
      canAddPayment: payload?.canAddPayment,
      payments: Array.isArray(payload?.payments) ? payload.payments : [],
    }));
  };

  const reloadExpenseRowsAndTotals = async () => {
    await Promise.all([reloadExpenseRows(), reloadTotals()]);
  };

  const reloadPaymentsAndTotals = async () => {
    await Promise.all([reloadPayments(), reloadTotals()]);
  };

  useEffect(() => {
    if (!isNewTransaction) return undefined;

    let isActive = true;

    setHeaderDialogMode("create");
    setIsHeaderDialogOpen(true);

    const fetchCreateAttachmentTemplates = async () => {
      try {
        const filesRes = await getFiles(getAttachmentRequest(null));
        if (!isActive) return;

        initializeAttachments(filesRes?.data);
        filesControl.setIsDirty?.(false);
      } catch (filesError) {
        if (!isActive) return;

        initializeAttachments([]);
        enqueueSnackbar(
          filesError?.message ?? "Δεν φορτώθηκαν τα συνημμένα.",
          { variant: "warning" },
        );
      }
    };

    fetchCreateAttachmentTemplates();

    return () => {
      isActive = false;
    };
  }, [isNewTransaction]);

  useEffect(() => {
    if (isNewTransaction) return undefined;

    let isActive = true;

    const fetchTransaction = async () => {
      setHasLoaded(false);
      setErrorMessage(null);
      setTransaction(null);
      setExpenseRows([]);
      setTransTypeLookups([]);
      setLoading(true);

      try {
        const res = await getTransHeaderExpense(transHeaderId);
        if (!isActive) return;

        const payload = getResponsePayload(res);

        if (isErrorResponse(res) || !payload) {
          setErrorMessage(
            getResponseMessage(
              res,
              "Δεν βρέθηκαν στοιχεία για το παραστατικό.",
            ),
          );
          return;
        }

        setTransaction(payload);

        try {
          const transTypesRes = await getTransTypes(false);
          if (!isActive) return;

          if (isErrorResponse(transTypesRes)) {
            setTransTypeLookups([]);
            enqueueSnackbar(
              getResponseMessage(
                transTypesRes,
                "Δεν φορτώθηκαν οι κανόνες πεδίων του είδους παραστατικού.",
              ),
              { variant: "warning" },
            );
          } else {
            const nextTransTypeLookups = getTransTypeLookupPayload(transTypesRes);
            setTransTypeLookups(nextTransTypeLookups);

            if (
              nextTransTypeLookups.length > 0 &&
              !findTransTypeLookup(nextTransTypeLookups, payload?.transTypeId)
            ) {
              enqueueSnackbar(
                "Δεν βρέθηκαν οι κανόνες πεδίων για το είδος παραστατικού.",
                { variant: "warning" },
              );
            }
          }
        } catch (transTypesError) {
          if (!isActive) return;

          setTransTypeLookups([]);
          enqueueSnackbar(
            transTypesError?.message ??
            "Δεν φορτώθηκαν οι κανόνες πεδίων του είδους παραστατικού.",
            { variant: "warning" },
          );
        }

        try {
          const rowsRes = await getTransHeaderExpenseRows(transHeaderId);
          if (!isActive) return;

          if (isErrorResponse(rowsRes)) {
            setExpenseRows([]);
            enqueueSnackbar(
              getResponseMessage(rowsRes, "Δεν φορτώθηκαν οι γραμμές δαπάνης."),
              { variant: "warning" },
            );
          } else {
            setExpenseRows(getRowsPayload(rowsRes));
          }
        } catch (rowsError) {
          if (!isActive) return;

          setExpenseRows([]);
          enqueueSnackbar(
            rowsError?.message ?? "Δεν φορτώθηκαν οι γραμμές δαπάνης.",
            { variant: "warning" },
          );
        }

        try {
          await fetchAttachments();
        } catch (filesError) {
          if (!isActive) return;

          initializeAttachments([]);
          enqueueSnackbar(
            filesError?.message ?? "Δεν φορτώθηκαν τα συνημμένα.",
            { variant: "warning" },
          );
        }
      } catch (error) {
        if (!isActive) return;

        setErrorMessage(
          error?.message ?? "Προέκυψε σφάλμα κατά τη φόρτωση του παραστατικού.",
        );
      } finally {
        if (isActive) {
          setHasLoaded(true);
          setLoading(false);
        }
      }
    };

    fetchTransaction();

    return () => {
      isActive = false;
    };
  }, [isNewTransaction, setLoading, transHeaderId]);

  useEffect(() => {
    if (
      !shouldOpenExpenseDetailAfterHeaderCreate ||
      isNewTransaction ||
      !hasLoaded ||
      !transaction
    ) {
      return;
    }

    if (canAddExpenseRow && !expenseDialogState.open) {
      setExpenseDialogState({
        open: true,
        mode: "create",
        row: null,
      });
    }

    const nextState = { ...(location.state ?? {}) };
    delete nextState[OPEN_EXPENSE_DETAIL_DIALOG_STATE_KEY];
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: nextState,
    });
  }, [
    canAddExpenseRow,
    expenseDialogState.open,
    hasLoaded,
    isNewTransaction,
    location.pathname,
    location.search,
    location.state,
    navigate,
    shouldOpenExpenseDetailAfterHeaderCreate,
    transaction,
  ]);

  const handleCancelHeader = () => {
    setIsHeaderDialogOpen(false);

    if (headerDialogMode === "create") {
      if (applicationId) {
        navigate(`/expenses/${applicationId}`);
        return;
      }

      navigate("/expenses");
    }
  };

  const hasPendingAttachmentChanges = useCallback(
    () => filesControl.isDirty || getUnsavedBundleIds(fileGroups).length > 0,
    [fileGroups, filesControl.isDirty],
  );

  const performBackNavigation = useCallback(() => {
    const historyIndex = window.history.state?.idx;

    if (typeof historyIndex === "number" && historyIndex > 0) {
      navigate(-1);
      return;
    }

    const fallbackApplicationId = transaction?.applicationId ?? applicationId;

    if (fallbackApplicationId) {
      navigate(`/expenses/${fallbackApplicationId}`);
      return;
    }

    navigate("/expenses");
  }, [applicationId, navigate, transaction?.applicationId]);

  const handleBackNavigation = useCallback(() => {
    if (hasPendingAttachmentChanges()) {
      setIsUnsavedAttachmentsBackWarningOpen(true);
      return;
    }

    performBackNavigation();
  }, [hasPendingAttachmentChanges, performBackNavigation]);

  const handleCloseUnsavedAttachmentsBackWarning = () => {
    setIsUnsavedAttachmentsBackWarningOpen(false);
  };

  const handleConfirmUnsavedAttachmentsBackNavigation = () => {
    setIsUnsavedAttachmentsBackWarningOpen(false);
    performBackNavigation();
  };

  const saveCreateAttachments = async (targetTransHeaderId) => {
    if (!targetTransHeaderId) return false;

    const unsavedBundleIds = getUnsavedBundleIds(fileGroups);
    const res = await filesControl.uploadFileGroups();

    if (isErrorResponse(res)) {
      enqueueSnackbar(
        getResponseMessage(res, "Δεν αποθηκεύτηκαν τα συνημμένα."),
        { variant: "error" },
      );
      return false;
    }

    const uploadedBundleIds = getBundleIdsFromUploadResponse(res);
    const bundleIds =
      uploadedBundleIds.length > 0 ? uploadedBundleIds : unsavedBundleIds;

    for (const bundleId of bundleIds) {
      const bundleRes = await addTransHeaderExpenseBundle({
        transHeaderId: targetTransHeaderId,
        bundleId,
      });

      if (isErrorResponse(bundleRes)) {
        enqueueSnackbar(
          getResponseMessage(
            bundleRes,
            "Δεν συνδέθηκε το συνημμένο με τη δαπάνη.",
          ),
          { variant: "error" },
        );
        return false;
      }
    }

    filesControl.setIsDirty?.(false);
    return true;
  };

  const savePendingAttachments = async (
    targetTransHeaderId,
    { refreshAttachments = false, showSuccessMessage = false } = {},
  ) => {
    if (!targetTransHeaderId) return false;

    const unsavedBundleIds = getUnsavedBundleIds(fileGroups);
    const shouldSaveAttachments =
      filesControl.isDirty || unsavedBundleIds.length > 0;

    if (!shouldSaveAttachments) return true;

    const res = await filesControl.uploadFileGroups();

    if (isErrorResponse(res)) {
      enqueueSnackbar(
        getResponseMessage(res, "Δεν αποθηκεύτηκαν τα συνημμένα."),
        { variant: "error" },
      );
      return false;
    }

    const uploadedBundleIds = getBundleIdsFromUploadResponse(res);
    const bundleIds =
      uploadedBundleIds.length > 0 ? uploadedBundleIds : unsavedBundleIds;

    for (const bundleId of bundleIds) {
      const bundleRes = await addTransHeaderExpenseBundle({
        transHeaderId: targetTransHeaderId,
        bundleId,
      });

      if (isErrorResponse(bundleRes)) {
        enqueueSnackbar(
          getResponseMessage(
            bundleRes,
            "Δεν συνδέθηκε το συνημμένο με τη δαπάνη.",
          ),
          { variant: "error" },
        );
        return false;
      }
    }

    if (refreshAttachments) {
      await fetchAttachments();
    }

    filesControl.setIsDirty?.(false);

    if (showSuccessMessage) {
      enqueueSnackbar("Τα συνημμένα αποθηκεύτηκαν.", {
        variant: "success",
      });
    }

    return true;
  };

  const handleSaveHeader = async (formValues) => {
    const currentApplicationId = transaction?.applicationId ?? applicationId;

    if (!currentApplicationId) {
      enqueueSnackbar("Δεν βρέθηκε η αίτηση της ΜΗΔ.", { variant: "error" });
      return;
    }

    try {
      setIsHeaderSaving(true);
      setLoading(true);

      const payload = buildTransHeaderExpensePayload({
        formValues,
        applicationId: currentApplicationId,
        transHeaderId: headerDialogMode === "edit" ? transHeaderId : undefined,
      });
      const res =
        headerDialogMode === "edit"
          ? await updateTransHeaderExpense(payload)
          : await createTransHeaderExpense(payload);

      if (isErrorResponse(res)) {
        enqueueSnackbar(
          getResponseMessage(res, "Δεν αποθηκεύτηκε το παραστατικό."),
          { variant: "error" },
        );
        return;
      }

      if (headerDialogMode === "create") {
        const newTransHeaderId = getTransHeaderIdFromResponse(res);

        if (!newTransHeaderId) {
          enqueueSnackbar("Δεν επιστράφηκε αναγνωριστικό δαπάνης.", {
            variant: "error",
          });
          return;
        }

        enqueueSnackbar("Η δαπάνη δημιουργήθηκε επιτυχώς.", {
          variant: "success",
        });
        const shouldSaveCreateAttachments =
          filesControl.isDirty || getUnsavedBundleIds(fileGroups).length > 0;

        if (shouldSaveCreateAttachments) {
          const attachmentsSaved = await saveCreateAttachments(newTransHeaderId);

          if (!attachmentsSaved) {
            enqueueSnackbar(
              "Η δαπάνη δημιουργήθηκε, αλλά δεν αποθηκεύτηκαν τα συνημμένα.",
              { variant: "warning" },
            );
          }
        }

        setIsHeaderDialogOpen(false);
        navigate(
          `/expenses/${currentApplicationId}/transactions/${newTransHeaderId}`,
          { state: { openExpenseDetailDialog: true } },
        );
        return;
      }

      enqueueSnackbar("Τα στοιχεία παραστατικού αποθηκεύτηκαν.", {
        variant: "success",
      });
      setIsHeaderDialogOpen(false);
      await Promise.all([reloadHeader(), reloadPaymentsAndTotals()]);
    } catch (error) {
      enqueueSnackbar(
        error?.message ?? "Προέκυψε σφάλμα κατά την αποθήκευση παραστατικού.",
        { variant: "error" },
      );
    } finally {
      setIsHeaderSaving(false);
      setLoading(false);
    }
  };

  const handleOpenEditHeader = async () => {
    if (!transHeaderId) return;

    try {
      setLoading(true);
      const res = await canChangeTransHeaderExpenseType(transHeaderId);
      const payload = getResponsePayload(res);

      if (isErrorResponse(res)) {
        setTypeChangeState({
          can: false,
          message: getResponseMessage(
            res,
            "Δεν είναι διαθέσιμος ο έλεγχος αλλαγής είδους παραστατικού.",
          ),
        });
      } else {
        setTypeChangeState({
          can: payload?.can !== false,
          message: payload?.message ?? null,
        });
      }
    } catch (error) {
      setTypeChangeState({
        can: false,
        message:
          error?.message ??
          "Δεν είναι διαθέσιμος ο έλεγχος αλλαγής είδους παραστατικού.",
      });
    } finally {
      setLoading(false);
      setHeaderDialogMode("edit");
      setIsHeaderDialogOpen(true);
    }
  };

  const handleDeleteTransaction = async (force = false) => {
    if (!transHeaderId) return;

    try {
      setLoading(true);
      const res = await deleteTransHeaderExpense(transHeaderId, force);
      const payload = getResponsePayload(res);
      const message = getResponseMessage(
        res,
        "Δεν ολοκληρώθηκε η διαγραφή παραστατικού.",
      );

      if (isErrorResponse(res) || payload?.canDelete === false) {
        enqueueSnackbar(message, { variant: "error" });
        return;
      }

      if (
        isWarningResponse(res) ||
        (payload?.requiresConfirmation === true && payload?.deleted !== true)
      ) {
        setDeleteConfirmation(message);
        return;
      }

      if (payload?.deleted === true) {
        enqueueSnackbar(message, { variant: "success" });
        setDeleteConfirmation(null);
        navigate(`/expenses/${transaction?.applicationId ?? applicationId}`);
        return;
      }

      enqueueSnackbar(message, { variant: "warning" });
    } catch (error) {
      enqueueSnackbar(
        error?.message ?? "Προέκυψε σφάλμα κατά τη διαγραφή παραστατικού.",
        { variant: "error" },
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitTransaction = async () => {
    if (!transHeaderId) return;

    try {
      setLoading(true);

      if (hasPendingAttachmentChanges()) {
        const attachmentsSaved = await savePendingAttachments(transHeaderId, {
          refreshAttachments: true,
        });

        if (!attachmentsSaved) return;
      }

      const res = await submitTransHeaderExpense({ transHeaderId });
      const payload = getResponsePayload(res);
      const message = getValidationMessage(
        res,
        "Δεν ολοκληρώθηκε η οριστική υποβολή.",
      );

      if (isErrorResponse(res) || payload?.changed === false) {
        enqueueSnackbar(message, { variant: "error" });
        return;
      }

      enqueueSnackbar(message, { variant: "success" });
      await Promise.all([
        reloadHeader(),
        reloadExpenseRowsAndTotals(),
        reloadPayments(),
      ]);
    } catch (error) {
      enqueueSnackbar(
        getValidationMessage(
          error,
          "Προέκυψε σφάλμα κατά την οριστική υποβολή.",
        ),
        { variant: "error" },
      );
    } finally {
      setLoading(false);
    }
  };

  const persistReviewDraft = async ({ showSuccess = true } = {}) => {
    if (!transHeaderId) return false;

    const validationMessage = validateReviewRows(expenseRows);
    if (validationMessage) {
      enqueueSnackbar(validationMessage, { variant: "error" });
      return false;
    }

    const reviewRowsPayload = buildReviewRowsPayload(expenseRows);
    const res = await saveTransHeaderExpenseReview({
      transHeaderId,
      rows: reviewRowsPayload,
    });

    if (isErrorResponse(res)) {
      enqueueSnackbar(getResponseMessage(res, "Δεν αποθηκεύτηκε ο έλεγχος."), {
        variant: "error",
      });
      return false;
    }

    const savedRows = getRowsPayload(res);
    if (savedRows.length > 0) {
      setExpenseRows(mergeRowsWithReviewDraft(savedRows, expenseRows));
    } else {
      await reloadExpenseRows();
    }

    await reloadTotals();

    if (showSuccess) {
      enqueueSnackbar("Ο έλεγχος αποθηκεύτηκε.", { variant: "success" });
    }

    return true;
  };

  const handleSaveReview = async () => {
    try {
      setIsReviewSaving(true);
      setLoading(true);
      await persistReviewDraft();
    } catch (error) {
      enqueueSnackbar(
        error?.message ?? "Προέκυψε σφάλμα κατά την αποθήκευση ελέγχου.",
        { variant: "error" },
      );
    } finally {
      setIsReviewSaving(false);
      setLoading(false);
    }
  };

  const handleReviewRowChange = useCallback((rowId, patch) => {
    setExpenseRows((currentRows) =>
      currentRows.map((row) => {
        if (row.id !== rowId) return row;

        return {
          ...row,
          ...patch,
        };
      }),
    );
  }, []);

  const handleOpenReviewDialog = () => {
    setReviewDialogState({
      open: true,
      statusId: getInferredFinalReviewStatusId(expenseRows),
      comments: "",
    });
  };

  const handleCloseReviewDialog = () => {
    if (isFinalReviewSaving) return;

    setReviewDialogState({
      open: false,
      statusId: "",
      comments: "",
    });
  };

  const handleConfirmFinalReview = async (values = {}) => {
    if (!transHeaderId) return;

    const statusId = normalizeReviewStatusId(
      values.statusId ?? reviewDialogState.statusId,
    );

    if (!statusId) {
      enqueueSnackbar("Επιλέξτε τελική κατάσταση ελέγχου.", {
        variant: "error",
      });
      return;
    }

    const hasPendingRows = expenseRows.some(
      (row) => !normalizeReviewStatusId(row?.expStatusId),
    );

    if (hasPendingRows) {
      enqueueSnackbar(
        "Υπάρχουν γραμμές σε κατάσταση «Προς έλεγχο». Ολοκληρώστε τον έλεγχο όλων των γραμμών.",
        { variant: "error" },
      );
      return;
    }

    try {
      setIsFinalReviewSaving(true);
      setLoading(true);

      const reviewSaved = await persistReviewDraft({ showSuccess: false });
      if (!reviewSaved) return;

      const res = await reviewTransHeaderExpense({
        transHeaderId,
        statusId,
        statusComments: emptyToNull(
          values.comments ?? reviewDialogState.comments,
        ),
      });
      const payload = getResponsePayload(res);
      const message = getValidationMessage(res, "Δεν ολοκληρώθηκε ο έλεγχος.");

      if (isErrorResponse(res) || payload?.changed === false) {
        enqueueSnackbar(message, { variant: "error" });
        return;
      }

      enqueueSnackbar(message, { variant: "success" });
      setReviewDialogState({
        open: false,
        statusId: "",
        comments: "",
      });
      await Promise.all([
        reloadHeader(),
        reloadExpenseRowsAndTotals(),
        reloadPayments(),
      ]);
    } catch (error) {
      enqueueSnackbar(
        getValidationMessage(
          error,
          "Προέκυψε σφάλμα κατά την ολοκλήρωση ελέγχου.",
        ),
        { variant: "error" },
      );
    } finally {
      setIsFinalReviewSaving(false);
      setLoading(false);
    }
  };

  const handleOpenCancelDialog = () => {
    setCancelDialogState({ open: true, comments: "" });
  };

  const handleCloseCancelDialog = () => {
    if (isCancelSaving) return;

    setCancelDialogState({ open: false, comments: "" });
  };

  const handleConfirmCancelTransaction = async () => {
    if (!transHeaderId) return;

    try {
      setIsCancelSaving(true);
      setLoading(true);

      const res = await cancelTransHeaderExpense({
        transHeaderId,
        statusComments: emptyToNull(cancelDialogState.comments),
      });
      const payload = getResponsePayload(res);
      const message = getValidationMessage(res, "Δεν ολοκληρώθηκε η ακύρωση.");

      if (isErrorResponse(res) || payload?.changed === false) {
        enqueueSnackbar(message, { variant: "error" });
        return;
      }

      enqueueSnackbar(message, { variant: "success" });
      setCancelDialogState({ open: false, comments: "" });
      await Promise.all([
        reloadHeader(),
        reloadExpenseRowsAndTotals(),
        reloadPayments(),
      ]);
    } catch (error) {
      enqueueSnackbar(
        getValidationMessage(error, "Προέκυψε σφάλμα κατά την ακύρωση."),
        { variant: "error" },
      );
    } finally {
      setIsCancelSaving(false);
      setLoading(false);
    }
  };

  const handleOpenChangeStatusDialog = () => {
    setIsChangeStatusDialogOpen(true);
  };

  const handleCloseChangeStatusDialog = () => {
    if (isChangeStatusSaving) return;

    setIsChangeStatusDialogOpen(false);
  };

  const handleConfirmChangeStatus = async ({ newStatusId, comments } = {}) => {
    if (!transHeaderId) {
      return {
        ok: false,
        message: "Δεν βρέθηκε αναγνωριστικό παραστατικού.",
      };
    }

    try {
      setIsChangeStatusSaving(true);
      setLoading(true);

      const res = await amendTransHeaderExpense({
        transHeaderId,
        newStatusId,
        comments: emptyToNull(comments),
      });
      const payload = getResponsePayload(res);
      const message = getValidationMessage(
        res,
        "Δεν ολοκληρώθηκε η αλλαγή κατάστασης.",
      );

      if (
        isErrorResponse(res) ||
        payload?.changed === false ||
        payload?.Changed === false
      ) {
        return { ok: false, message };
      }

      enqueueSnackbar(message, { variant: "success" });
      setIsChangeStatusDialogOpen(false);
      await Promise.all([
        reloadHeader(),
        reloadExpenseRowsAndTotals(),
        reloadPayments(),
      ]);

      return { ok: true };
    } catch (error) {
      return {
        ok: false,
        message: getValidationMessage(
          error,
          "Προέκυψε σφάλμα κατά την αλλαγή κατάστασης.",
        ),
      };
    } finally {
      setIsChangeStatusSaving(false);
      setLoading(false);
    }
  };

  const handleOpenDeleteWarning = () => {
    setIsDeleteWarningOpen(true);
  };

  const handleCloseDeleteWarning = () => {
    setIsDeleteWarningOpen(false);
  };

  const handleConfirmDeleteWarning = () => {
    setIsDeleteWarningOpen(false);
    handleDeleteTransaction(false);
  };

  const handleOpenCreateExpenseRow = useCallback(() => {
    if (!canAddExpenseRow || isShortcutBlocked) return;

    setExpenseDialogState({
      open: true,
      mode: "create",
      row: null,
    });
  }, [canAddExpenseRow, isShortcutBlocked]);

  const handleOpenEditExpenseRow = (row) => {
    setExpenseDialogState({
      open: true,
      mode: "edit",
      row,
    });
  };

  const handleCloseExpenseDialog = () => {
    setExpenseDialogState((currentState) => ({
      ...currentState,
      open: false,
    }));
  };

  const handleSaveExpenseRow = async (payload) => {
    try {
      setIsExpenseRowSaving(true);
      setLoading(true);

      const res =
        expenseDialogState.mode === "edit"
          ? await updateTransHeaderExpenseRow(payload)
          : await createTransHeaderExpenseRow(payload);

      if (isErrorResponse(res)) {
        enqueueSnackbar(
          getResponseMessage(res, "Δεν αποθηκεύτηκε η γραμμή δαπάνης."),
          { variant: "error" },
        );
        return;
      }

      enqueueSnackbar("Η γραμμή δαπάνης αποθηκεύτηκε.", {
        variant: "success",
      });
      setExpenseDialogState({ open: false, mode: "create", row: null });
      await reloadExpenseRowsAndTotals();
    } catch (error) {
      enqueueSnackbar(
        error?.message ??
        "Προέκυψε σφάλμα κατά την αποθήκευση γραμμής δαπάνης.",
        { variant: "error" },
      );
    } finally {
      setIsExpenseRowSaving(false);
      setLoading(false);
    }
  };

  const handleConfirmDeleteExpenseRow = async () => {
    if (!expenseRowDeleteCandidate?.id) return;

    try {
      setLoading(true);
      const res = await deleteTransHeaderExpenseRow(
        expenseRowDeleteCandidate.id,
      );
      const payload = getResponsePayload(res);
      const message = getResponseMessage(
        res,
        "Δεν διαγράφηκε η γραμμή δαπάνης.",
      );

      if (isErrorResponse(res) || payload?.deleted === false) {
        enqueueSnackbar(message, { variant: "error" });
        return;
      }

      enqueueSnackbar(message, { variant: "success" });
      setExpenseRowDeleteCandidate(null);
      await reloadExpenseRowsAndTotals();
    } catch (error) {
      enqueueSnackbar(
        error?.message ?? "Προέκυψε σφάλμα κατά τη διαγραφή γραμμής δαπάνης.",
        { variant: "error" },
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreatePayment = useCallback(() => {
    if (!canAddPayment || isShortcutBlocked) return;

    setPaymentDialogState({
      open: true,
      mode: "create",
      payment: null,
    });
  }, [canAddPayment, isShortcutBlocked]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
        return;
      }

      if (isShortcutTypingTarget(event.target) || hasOpenShortcutOverlay()) {
        return;
      }

      if (event.code === "KeyN") {
        if (!canAddExpenseRow || isShortcutBlocked) return;

        event.preventDefault();
        handleOpenCreateExpenseRow();
        return;
      }

      if (event.code === "KeyP") {
        if (!canAddPayment || isShortcutBlocked) return;

        event.preventDefault();
        handleOpenCreatePayment();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    canAddExpenseRow,
    canAddPayment,
    handleOpenCreateExpenseRow,
    handleOpenCreatePayment,
    isShortcutBlocked,
  ]);

  const handleOpenEditPayment = async (payment) => {
    if (!payment?.paymentTransHeaderId) return;

    try {
      setLoading(true);
      const res = await getTransHeaderExpensePayment(
        payment.paymentTransHeaderId,
      );

      if (isErrorResponse(res)) {
        enqueueSnackbar(
          getResponseMessage(res, "Δεν φορτώθηκαν τα στοιχεία πληρωμής."),
          { variant: "error" },
        );
        return;
      }

      const payload = getResponsePayload(res);

      if (!payload) {
        enqueueSnackbar("Δεν βρέθηκαν στοιχεία πληρωμής.", {
          variant: "error",
        });
        return;
      }

      setPaymentDialogState({
        open: true,
        mode: "edit",
        payment: payload,
      });
    } catch (error) {
      enqueueSnackbar(
        error?.message ?? "Προέκυψε σφάλμα κατά τη φόρτωση πληρωμής.",
        { variant: "error" },
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClosePaymentDialog = () => {
    setPaymentDialogState((currentState) => ({
      ...currentState,
      open: false,
    }));
  };

  const handleSavePayment = async (payload) => {
    try {
      setIsPaymentSaving(true);
      setLoading(true);

      const res =
        paymentDialogState.mode === "edit"
          ? await updateTransHeaderExpensePayment(payload)
          : await createTransHeaderExpensePayment(payload);

      if (isErrorResponse(res)) {
        enqueueSnackbar(
          getResponseMessage(res, "Δεν αποθηκεύτηκε η πληρωμή."),
          { variant: "error" },
        );
        return;
      }

      enqueueSnackbar("Η πληρωμή αποθηκεύτηκε.", { variant: "success" });
      setPaymentDialogState({ open: false, mode: "create", payment: null });
      await reloadPaymentsAndTotals();
      setPaymentAttachmentsRefreshKey((currentKey) => currentKey + 1);
    } catch (error) {
      enqueueSnackbar(
        error?.message ?? "Προέκυψε σφάλμα κατά την αποθήκευση πληρωμής.",
        { variant: "error" },
      );
    } finally {
      setIsPaymentSaving(false);
      setLoading(false);
    }
  };

  const handleConfirmDeletePayment = async () => {
    if (!paymentDeleteCandidate?.paymentTransHeaderId) return;

    try {
      setLoading(true);
      const res = await deleteTransHeaderExpensePayment(
        paymentDeleteCandidate.paymentTransHeaderId,
      );
      const payload = getResponsePayload(res);
      const message = getResponseMessage(res, "Δεν διαγράφηκε η πληρωμή.");

      if (isErrorResponse(res) || payload?.deleted === false) {
        enqueueSnackbar(message, { variant: "error" });
        return;
      }

      enqueueSnackbar(message, { variant: "success" });
      setPaymentDeleteCandidate(null);
      await reloadPaymentsAndTotals();
    } catch (error) {
      enqueueSnackbar(
        error?.message ?? "Προέκυψε σφάλμα κατά τη διαγραφή πληρωμής.",
        { variant: "error" },
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAction = (actionKey, setting) => {
    if (actionKey === "submit") {
      handleSubmitTransaction();
      return;
    }

    if (actionKey === "saveReview") {
      handleSaveReview();
      return;
    }

    if (actionKey === "review") {
      handleOpenReviewDialog();
      return;
    }

    if (actionKey === "cancel") {
      handleOpenCancelDialog();
      return;
    }

    if (actionKey === "changeStatus") {
      handleOpenChangeStatusDialog();
      return;
    }

    if (actionKey === "editHeader") {
      handleOpenEditHeader();
      return;
    }

    if (actionKey === "delete") {
      handleOpenDeleteWarning();
      return;
    }

    if (actionKey === "addExpense") {
      handleOpenCreateExpenseRow();
      return;
    }

    const actionText = setting?.txt ?? actionKey;

    enqueueSnackbar(
      `Η ενέργεια "${actionText}" θα συνδεθεί με backend endpoint σε επόμενο βήμα.`,
      { variant: "info" },
    );
  };

  const handlePaymentAction = (actionKey, payment) => {
    if (actionKey === "addPayment") {
      handleOpenCreatePayment();
      return;
    }

    if (actionKey === "editPayment") {
      handleOpenEditPayment(payment);
      return;
    }

    if (actionKey === "deletePayment") {
      setPaymentDeleteCandidate(payment);
      return;
    }

    const actionText =
      actionKey === "addPayment" ? "Προσθήκη Πληρωμής" : "Επεξεργασία Πληρωμής";

    enqueueSnackbar(
      `Η ενέργεια "${actionText}" θα συνδεθεί με backend endpoint σε επόμενο βήμα.`,
      { variant: "info" },
    );
  };

  const handleExpenseRowAction = (actionKey, row) => {
    if (actionKey === "edit") {
      handleOpenEditExpenseRow(row);
      return;
    }

    if (actionKey === "delete") {
      setExpenseRowDeleteCandidate(row);
      return;
    }

    const actionText =
      actionKey === "delete" ? "Διαγραφή Δαπάνης" : "Επεξεργασία Δαπάνης";

    enqueueSnackbar(
      `Η ενέργεια "${actionText}" θα συνδεθεί με backend endpoint σε επόμενο βήμα.`,
      { variant: "info" },
    );
  };

  const handleSaveAttachments = async () => {
    if (!transHeaderId) {
      enqueueSnackbar("Δεν βρέθηκε αναγνωριστικό δαπάνης.", {
        variant: "error",
      });
      return;
    }

    try {
      setIsAttachmentSaving(true);
      setLoading(true);

      await savePendingAttachments(transHeaderId, {
        refreshAttachments: true,
        showSuccessMessage: true,
      });
    } catch (error) {
      enqueueSnackbar(
        error?.message ?? "Προέκυψε σφάλμα κατά την αποθήκευση συνημμένων.",
        { variant: "error" },
      );
    } finally {
      setIsAttachmentSaving(false);
      setLoading(false);
    }
  };

  const renderDetails = () => {
    if (isNewTransaction) return null;

    if (!hasLoaded) {
      return (
        <Stack spacing={1.5}>
          <Skeleton variant="rounded" height={160} />
          <Skeleton variant="rounded" height={52} />
          <Skeleton variant="rounded" height={260} />
        </Stack>
      );
    }

    if (errorMessage) {
      return <Alert severity="error">{errorMessage}</Alert>;
    }

    if (!transaction) return null;

    const payments = transaction?.payments ?? [];
    const expenseRowsSubtitle = getSectionSummaryText(
      expenseRows.length,
      "γραμμή",
      "γραμμές",
      sumSectionAmounts(expenseRows, "totalAmount"),
    );
    const paymentsSubtitle = getSectionSummaryText(
      payments.length,
      "πληρωμή",
      "πληρωμές",
      sumSectionAmounts(payments, "amount"),
    );
    const addExpenseSetting = transaction?.settings?.addExpense;
    const addExpenseText = addExpenseSetting?.txt ?? "Προσθήκη Δαπάνης";

    return (
      <Stack spacing={1.5}>
        <ExpenseTransactionHeader
          transaction={transaction}
          modeConfig={expenseModeConfig}
          actionsDisabled={expenseActionsDisabled}
          onHeaderAction={handleAction}
        />
        <ExpenseSectionAccordion
          title="ΑΝΑΛΥΣΗ ΔΑΠΑΝΩΝ"
          subtitle={expenseRowsSubtitle}
          collapsedAction={
            canAddExpenseRow ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                disabled={expenseActionsDisabled}
                onClick={(event) => {
                  event.stopPropagation();
                  handleAction("addExpense", addExpenseSetting);
                }}
                title={`${addExpenseText} (Alt+N)`}
                sx={{
                  fontWeight: 800,
                  minHeight: 30,
                  whiteSpace: "nowrap",
                }}
              >
                {addExpenseText}
              </Button>
            ) : null
          }
        >
          <ExpenseDetailsGrid
            rows={expenseRows}
            canEdit={transaction?.settings?.canEdit === true}
            showApprovedAmount={showApprovedAmount}
            showReviewStatus={showReviewStatus}
            canReview={canReview}
            modeConfig={expenseModeConfig}
            addExpenseSetting={addExpenseSetting}
            isAddExpenseDisabled={expenseActionsDisabled}
            onAddExpense={() =>
              handleAction("addExpense", addExpenseSetting)
            }
            onRowAction={handleExpenseRowAction}
            onReviewRowChange={handleReviewRowChange}
          />
        </ExpenseSectionAccordion>
        <ExpenseSectionAccordion title="ΠΛΗΡΩΜΕΣ" subtitle={paymentsSubtitle}>
          <ExpensePaymentsPanel
            transaction={transaction}
            onPaymentAction={handlePaymentAction}
          />
        </ExpenseSectionAccordion>
        <ExpenseAttachments
          settings={transaction?.settings?.attachments}
          isSaving={isAttachmentSaving}
          onSave={handleSaveAttachments}
        />
        {!isTrueFlag(transaction?.paidOnIssue) && (
          <PaymentAttachmentsReadOnly
            payments={transaction?.payments}
            refreshKey={paymentAttachmentsRefreshKey}
          />
        )}
        <ExpenseActions
          settings={transaction?.settings}
          disabled={expenseActionsDisabled}
          onAction={handleAction}
          onBack={handleBackNavigation}
        />
      </Stack>
    );
  };

  return (
    <Box
      sx={{
        m: 2.5,
        p: 2.5,
        minHeight: "calc(100vh - 150px)",
        borderRadius: 1,
        backgroundColor: "#fefefe",
      }}
    >
      {renderDetails()}

      <ExpenseHeaderDialog
        open={isHeaderDialogOpen}
        mhdName={mhdName}
        isCreateMode={headerDialogMode === "create"}
        initialValues={headerDialogMode === "edit" ? transaction : null}
        isSaving={isHeaderSaving}
        isTypeChangeAllowed={typeChangeState.can}
        typeChangeMessage={typeChangeState.message}
        onCancel={handleCancelHeader}
        onSave={handleSaveHeader}
      />

      <ExpenseDetailDialog
        open={expenseDialogState.open}
        mode={expenseDialogState.mode}
        applicationId={transaction?.applicationId ?? applicationId}
        transHeaderId={transHeaderId}
        transTypeId={transaction?.transTypeId}
        defaultDateFrom={transaction?.dateFrom}
        defaultDateTo={transaction?.dateTo}
        settings={{
          ...(transaction?.settings ?? {}),
          showApprovedAmount,
        }}
        initialValues={expenseDialogState.row}
        isSaving={isExpenseRowSaving}
        title={getExpenseDetailDialogTitle(transaction, mhdName)}
        onCancel={handleCloseExpenseDialog}
        onSave={handleSaveExpenseRow}
      />

      <PaymentDialog
        open={paymentDialogState.open}
        mode={paymentDialogState.mode}
        expTransHeaderId={transHeaderId}
        mhdName={mhdName}
        initialValues={paymentDialogState.payment}
        attachmentSettings={transaction?.settings?.attachments}
        paidOnIssue={transaction?.paidOnIssue}
        isSaving={isPaymentSaving}
        onCancel={handleClosePaymentDialog}
        onSave={handleSavePayment}
      />

      <FinalReviewDialog
        open={reviewDialogState.open}
        statusId={reviewDialogState.statusId}
        comments={reviewDialogState.comments}
        commentsOn={transaction?.settings?.review?.commentsOn}
        isSaving={isFinalReviewSaving}
        onCancel={handleCloseReviewDialog}
        onConfirm={handleConfirmFinalReview}
      />

      <ChangeStatusDialog
        open={isChangeStatusDialogOpen}
        transHeaderId={transHeaderId}
        title={
          [transaction?.transTypeName, transaction?.transNumber]
            .filter(Boolean)
            .join(" - ") || mhdName
        }
        currentStatus={transaction?.statusAbbr ?? transaction?.statusName ?? ""}
        isSaving={isChangeStatusSaving}
        onCancel={handleCloseChangeStatusDialog}
        onConfirm={handleConfirmChangeStatus}
      />

      <Dialog
        open={isUnsavedAttachmentsBackWarningOpen}
        onClose={handleCloseUnsavedAttachmentsBackWarning}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle variant="h4">Μη αποθηκευμένα συνημμένα</DialogTitle>
        <DialogContent>
          <Typography>
            Έχετε ανεβάσει συνημμένα που δεν έχουν αποθηκευτεί. Αν επιστρέψετε,
            οι αλλαγές στα συνημμένα θα χαθούν. Θέλετε να συνεχίσετε;
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={handleCloseUnsavedAttachmentsBackWarning}
          >
            Παραμονή
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleConfirmUnsavedAttachmentsBackNavigation}
          >
            Επιστροφή
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cancelDialogState.open}
        onClose={handleCloseCancelDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle variant="h4">Ακύρωση Παραστατικού</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography>
              Το παραστατικό θα ακυρωθεί χωρίς να διαγραφούν οι γραμμές,
              πληρωμές ή συνημμένα. Θέλετε να συνεχίσετε;
            </Typography>
            {transaction?.settings?.cancel?.commentsOn && (
              <TextField
                fullWidth
                multiline
                minRows={4}
                label="Σχόλια Ακύρωσης"
                value={cancelDialogState.comments}
                onChange={(event) =>
                  setCancelDialogState((currentState) => ({
                    ...currentState,
                    comments: event.target.value,
                  }))
                }
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={handleCloseCancelDialog}
            disabled={isCancelSaving}
          >
            Άκυρο
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmCancelTransaction}
            disabled={isCancelSaving}
          >
            Ακύρωση
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!expenseRowDeleteCandidate}
        onClose={() => setExpenseRowDeleteCandidate(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle variant="h4">Διαγραφή Γραμμής Δαπάνης</DialogTitle>
        <DialogContent>
          <Typography>
            Η γραμμή δαπάνης θα διαγραφεί οριστικά. Θέλετε να συνεχίσετε;
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => setExpenseRowDeleteCandidate(null)}
          >
            Άκυρο
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDeleteExpenseRow}
          >
            Διαγραφή
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!paymentDeleteCandidate}
        onClose={() => setPaymentDeleteCandidate(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle variant="h4">Διαγραφή Πληρωμής</DialogTitle>
        <DialogContent>
          <Typography>
            Η πληρωμή θα διαγραφεί οριστικά. Θέλετε να συνεχίσετε;
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => setPaymentDeleteCandidate(null)}
          >
            Άκυρο
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDeletePayment}
          >
            Διαγραφή
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isDeleteWarningOpen}
        onClose={handleCloseDeleteWarning}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle variant="h4">Προειδοποίηση Διαγραφής</DialogTitle>
        <DialogContent>
          <Typography>
            Η διαγραφή του παραστατικού είναι οριστική. Θέλετε να συνεχίσετε;
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleCloseDeleteWarning}>
            Άκυρο
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDeleteWarning}
          >
            Διαγραφή
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!deleteConfirmation}
        onClose={() => setDeleteConfirmation(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Επιβεβαίωση Διαγραφής</DialogTitle>
        <DialogContent>
          <Typography>{deleteConfirmation}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => setDeleteConfirmation(null)}
          >
            Άκυρο
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => handleDeleteTransaction(true)}
          >
            Διαγραφή
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const ExpenseTransactionDetails = () => {
  const { transHeaderId } = useParams();

  return (
    <FileProvider
      downloadFile={(fileId, type) => downloadFile(fileId, type)}
      uploadFiles={(fileGroups) => uploadFiles(fileGroups, null)}
      onDeleteBundle={async (bundleId) =>
        await deleteBundle(
          bundleId,
          EXPENSE_ATTACHMENTS_PROJECT_TABLE_ID,
          null,
          transHeaderId ?? null,
          null,
        )
      }
      onDeleteFile={async (fileId) => {
        await deleteFile(
          fileId,
          EXPENSE_ATTACHMENTS_PROJECT_TABLE_ID,
          null,
          transHeaderId ?? null,
          null,
        );
      }}
    >
      <ExpenseTransactionDetailsContent />
    </FileProvider>
  );
};

export default ExpenseTransactionDetails;
